import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import WizardPromptCard from './WizardPromptCard';
import { ProfileHero, ProfileNav } from './profile/ProfileHero';
import { SectionShell } from './profile/SectionShell';
import { AnalysisProgress } from './profile/AnalysisProgress';
import { OffersRead, FaqsRead, AudienceRead, VoiceRead, ProofRead, GuardrailsRead, LookRead } from './profile/ReadViews';
import { businessLayout, sectionFilled, SECTION_META } from './profile/businessLayout';
import {
  ArrowLeft, Globe, Loader2, Save, Sparkles, Star, Plus, Trash2, Check,
  Building2, Phone, Users, Shield, MessageCircleQuestion, Swords, Ban,
  Palette, FileText, ChevronDown, X, Quote, ExternalLink, Mic2, MoreVertical,
} from 'lucide-react';

/* ───────────────────────── helpers ───────────────────────── */

const ACCENTS = {
  identity: 'from-sky-400 to-blue-500',
  contact: 'from-teal-400 to-emerald-500',
  customers: 'from-indigo-400 to-violet-500',
  competitors: 'from-fuchsia-400 to-purple-500',
  do_not_say: 'from-rose-400 to-red-500',
  offerings: 'from-amber-400 to-orange-500',
  voice_tone: 'from-cyan-400 to-sky-500',
  faqs: 'from-violet-400 to-purple-500',
  proof: 'from-emerald-400 to-green-500',
  palette: 'from-pink-400 to-rose-500',
};

const SOURCE_STYLES = {
  website: { label: 'website', cls: 'bg-sky-400/10 text-sky-300 border-sky-400/20' },
  gbp: { label: 'Google', cls: 'bg-amber-400/10 text-amber-300 border-amber-400/20' },
  manual: { label: 'manual', cls: 'bg-teal-400/10 text-teal-300 border-teal-400/20' },
  onboarding: { label: 'onboarding', cls: 'bg-violet-400/10 text-violet-300 border-violet-400/20' },
};

export const SourceBadge = ({ source, testId }) => {
  const s = SOURCE_STYLES[source];
  if (!s) return null;
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={testId || `source-badge-${source}`}>{s.label}</span>;
};

const ConfidenceChip = ({ level }) => {
  if (!level || level === 'high') return null;
  return (
    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${level === 'low' ? 'bg-rose-400/10 text-rose-300' : 'bg-amber-400/10 text-amber-300'}`}>
      {level === 'low' ? 'verify' : 'check'}
    </span>
  );
};

export const ScoreRing = ({ score, size = 56, isDarkMode, loading = false, testId }) => {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  if (loading) {
    return (
      <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testId || 'brand-score-ring-loading'}>
        <svg width={size} height={size} className="animate-spin" style={{ animationDuration: '1.1s' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={size > 70 ? 6 : 5} stroke={isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={size > 70 ? 6 : 5} stroke="#2dd4bf" strokeLinecap="round"
            strokeDasharray={`${c * 0.28} ${c * 0.72}`} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center">
          <Globe size={size > 70 ? 18 : 13} className="text-teal-400 animate-pulse" />
        </span>
      </div>
    );
  }
  const color = score >= 70 ? '#34d399' : score >= 40 ? '#fbbf24' : '#f87171';
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testId || 'brand-score-ring'}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={size > 70 ? 6 : 5} stroke={isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={size > 70 ? 6 : 5} stroke={color} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * Math.min(score, 100)) / 100} style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-black" style={{ color, fontSize: size > 70 ? 17 : 13 }}>{score}%</span>
    </div>
  );
};

const Stars = ({ rating = 0, size = 14 }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={size} className={i <= Math.round(rating) ? 'text-amber-300 fill-amber-300' : 'text-gray-600'} />
    ))}
  </span>
);

const CHIP_STYLES = {
  teal: 'bg-teal-400/10 text-teal-300 border-teal-400/20',
  rose: 'bg-rose-400/10 text-rose-300 border-rose-400/20',
  indigo: 'bg-indigo-400/10 text-indigo-300 border-indigo-400/20',
  fuchsia: 'bg-fuchsia-400/10 text-fuchsia-300 border-fuchsia-400/20',
  cyan: 'bg-cyan-400/10 text-cyan-300 border-cyan-400/20',
  violet: 'bg-violet-400/10 text-violet-300 border-violet-400/20',
};

const ChipsInput = ({ values = [], onChange, placeholder, accent = 'teal', testId }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput('');
  };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v, i) => (
          <span key={i} className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${CHIP_STYLES[accent] || CHIP_STYLES.teal}`}>
            {v}
            <button onClick={() => onChange(values.filter((_, j) => j !== i))} className="opacity-60 hover:opacity-100"><X size={10} /></button>
          </span>
        ))}
      </div>
      <input value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border text-xs bg-white/5 border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-teal-400/50" />
    </div>
  );
};

const TONE_AXES = [['formality', 'Formality', 'Casual', 'Formal'], ['warmth', 'Warmth', 'Neutral', 'Warm'], ['energy', 'Energy', 'Calm', 'Energetic'], ['technicality', 'Technicality', 'Simple', 'Technical']];

const Group = ({ ctx, id, icon: Icon, title, count, collapsible = false, defaultOpen = false, span = '', delay = 0, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const body = <div className="px-5 md:px-6 pb-5 md:pb-6">{children}</div>;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}
      className={`${ctx.card} overflow-hidden ${span}`} data-testid={`brand-group-${id}`}>
      <div onClick={collapsible ? () => setOpen(v => !v) : undefined}
        className={`flex items-center gap-2.5 px-5 md:px-6 py-4 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        data-testid={`brand-group-header-${id}`}>
        <span className={`w-8 h-8 rounded-xl bg-gradient-to-br ${ACCENTS[id] || ACCENTS.identity} flex items-center justify-center shrink-0`}>
          <Icon size={14} className="text-white" />
        </span>
        <h3 className={`font-display text-[15px] font-semibold tracking-tight ${ctx.text}`}>{title}</h3>
        {count !== undefined && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ctx.isDarkMode ? 'bg-white/[0.07] text-gray-300' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <ConfidenceChip level={ctx.conf[id]} />
          <SourceBadge source={ctx.sources[id]} />
          {collapsible && <ChevronDown size={14} className={`${ctx.sub} transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />}
        </span>
      </div>
      {collapsible ? (
        <AnimatePresence initial={false}>
          {open && (
            <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }} className="overflow-hidden">
              {body}
            </motion.div>
          )}
        </AnimatePresence>
      ) : body}
    </motion.div>
  );
};

const Field = ({ ctx, section, k, label, long }) => (
  <div className="mb-3">
    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${ctx.sub}`}>{label}</p>
    {long ? (
      <textarea rows={2} value={(ctx.brand[section] || {})[k] || ''} onChange={e => ctx.setIn(section, k, e.target.value)} className={ctx.inputCls} data-testid={`brand-field-${section}-${k}`} />
    ) : (
      <input value={(ctx.brand[section] || {})[k] || ''} onChange={e => ctx.setIn(section, k, e.target.value)} className={ctx.inputCls} data-testid={`brand-field-${section}-${k}`} />
    )}
  </div>
);

const MISSING_LABELS = {
  identity: 'identity', offerings: 'offerings', voice_tone: 'voice & tone', faqs: 'FAQs',
  contact: 'contact', proof: 'proof', customers: 'customers', competitors: 'competitors', do_not_say: 'do-not-say',
};

/* ───────────────────────── main section ───────────────────────── */

export const BrandProfileSection = ({ api, subAccountId, profileId, isDarkMode, initialJob = null, onClose, onOpenOnboardingData, onScoreChange, onSendToWizard, projectMode = false }) => {
  const [info, setInfo] = useState(null);
  const [brand, setBrand] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzeOpen, setAnalyzeOpen] = useState(!!initialJob);
  const [url, setUrl] = useState('');
  const [price, setPrice] = useState(null);
  const [job, setJob] = useState(initialJob);
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState({});
  const pollRef = useRef(null);

  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const sub = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const card = `rounded-lg border ${border} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-white'} transition-colors`;
  const inputCls = `w-full px-3.5 py-2 rounded-xl border text-sm focus:outline-none focus:border-teal-400/60 ${
    isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/25' : 'bg-white border-gray-300 text-gray-900'}`;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}`);
      setInfo(res.data);
      setBrand(res.data.brand || {});
      if (res.data.analyzing_job_id) setJob(j => j || { job_id: res.data.analyzing_job_id, status: 'running', steps: [] });
      onScoreChange?.(res.data.score);
    } catch { toast.error('Could not load brand profile'); }
  }, [api, subAccountId, profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!info) return;
    const site = ((info.brand || {}).contact || {}).website || '';
    if (site) setUrl(u => u || site);
    if ((info.score || 0) < 10 && !job && !info.analyzing_job_id) setAnalyzeOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  useEffect(() => {
    if (analyzeOpen && price === null) {
      api.get(`/api/sub-accounts/${subAccountId}/brand-profiles/analyze-price`)
        .then(r => setPrice(r.data)).catch(() => setPrice({ price: 0.20, sufficient: true }));
    }
  }, [analyzeOpen, price, api, subAccountId]);

  const set = (section, value) => { setBrand(p => ({ ...p, [section]: value })); setDirty(true); };
  const setIn = (section, key, value) => set(section, { ...(brand[section] || {}), [key]: value });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}`, { brand });
      setDirty(false);
      onScoreChange?.(res.data.score);
      setInfo(p => ({ ...p, score: res.data.score, breakdown: res.data.breakdown }));
      toast.success(`Brand profile saved — score ${res.data.score}%`);
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const startAnalysis = async () => {
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}/analyze`, { url });
      setJob({ job_id: res.data.job_id, status: 'running', steps: [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start analysis'); }
  };

  useEffect(() => {
    if (job?.status === 'running') {
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}/analyze/${job.job_id}`);
          setJob(r.data);
          if (r.data.status !== 'running') {
            clearInterval(pollRef.current);
            if (r.data.status === 'done') { toast.success('Website analyzed — profile updated'); setJob(null); setAnalyzeOpen(false); load(); }
          }
        } catch { clearInterval(pollRef.current); }
      }, 1500);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.status, job?.job_id, api, subAccountId, profileId, load]);

  const deleteProfile = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}`);
      setConfirmDelete(false);
      toast.success('Brand profile deleted');
      onClose?.();
    } catch { toast.error('Delete failed'); } finally { setDeleting(false); }
  };

  if (!info) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-teal-400" size={26} /></div>;

  const sources = info.sources || {};
  const conf = brand.confidence || {};
  const identity = brand.identity || {};
  const customers = brand.customers || {};
  const tone = brand.voice_tone || {};
  const offerings = brand.offerings || [];
  const faqs = brand.faqs || [];
  const proof = brand.proof || {};
  const gbp = brand.gbp;
  const missing = Object.entries(info.breakdown || {}).filter(([, v]) => !v.filled).map(([k]) => MISSING_LABELS[k]).filter(Boolean);
  const sourceSet = [...new Set(Object.values(sources))];

  const ctx = { card, text, sub, conf, sources, brand, setIn, inputCls, isDarkMode };
  const running = job?.status === 'running';
  const layout = businessLayout(identity);
  const labels = { ...SECTION_META, offers: layout.offersLabel };
  const filled = Object.fromEntries(layout.order.map(id => [id, !!sectionFilled(id, brand)]));
  const visible = layout.order.filter(id => filled[id] || editing[id]);
  const missingTray = layout.order.filter(id => !filled[id] && !editing[id] && id !== 'look');
  const toggleEdit = id => {
    if (editing[id] && dirty) save();
    setEditing(e => ({ ...e, [id]: !e[id] }));
    if (!editing[id]) setTimeout(() => document.getElementById(`bp-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  const lbl = t => <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 mt-3 ${sub}`}>{t}</p>;
  const setList = (key, list) => set(key, list);
  const shell = (id, icon, accent, read, edit) => (
    <SectionShell id={id} title={labels[id]} icon={icon} accent={accent} isDarkMode={isDarkMode} editing={!!editing[id]}
      onToggle={id === 'look' ? null : () => toggleEdit(id)}>{editing[id] ? edit : read}</SectionShell>
  );
  const renderSection = id => {
    if (id === 'overview') return shell(id, Building2, 'teal',
      <div className="grid md:grid-cols-2 gap-x-10 gap-y-6" data-testid="bp-overview-read">
        {identity.mission && <div className="md:col-span-2"><p className={`text-[15px] leading-relaxed ${text}`}>{identity.mission}</p></div>}
        {[['Industry', identity.industry], ['Business model', identity.business_model], ['Service area', identity.service_area], ['Years in business', identity.years_in_business],
          ['Address', brand.contact?.address], ['Email', brand.contact?.email], ['Hours', brand.contact?.hours]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k}><p className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-1.5 ${sub}`}>{k}</p><p className={`text-[13.5px] leading-relaxed ${text}`}>{v}</p></div>
        ))}
      </div>,
      <div className="grid md:grid-cols-2 gap-x-6">
        {[['identity', 'business_name', 'Business name'], ['identity', 'business_model', 'Business model'], ['identity', 'tagline', 'Tagline'], ['identity', 'industry', 'Industry'],
          ['identity', 'years_in_business', 'Years in business'], ['identity', 'service_area', 'Service area'], ['contact', 'phone', 'Phone'], ['contact', 'email', 'Email'],
          ['contact', 'website', 'Website'], ['contact', 'address', 'Address']].map(([sec, k, l]) => <Field key={k} ctx={ctx} section={sec} k={k} label={l} />)}
        <div className="md:col-span-2"><Field ctx={ctx} section="identity" k="mission" label="Mission" long /><Field ctx={ctx} section="contact" k="hours" label="Hours" long /></div>
      </div>);
    if (id === 'offers') return shell(id, Sparkles, 'amber',
      <OffersRead offerings={offerings} tiered={layout.tiered} isDarkMode={isDarkMode} />,
      <div className="space-y-3" data-testid="offerings-table">
        {offerings.map((o, i) => (
          <div key={i} className={`grid md:grid-cols-[1fr_2fr_1fr_1fr_auto] gap-2 items-start pb-3 border-b ${border}`}>
            {['name', 'description', 'price', 'duration'].map(k => (
              <textarea key={k} rows={k === 'description' ? 2 : 1} value={o[k] || ''} placeholder={k} onChange={e => setList('offerings', offerings.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x))} className={inputCls} />
            ))}
            <button onClick={() => setList('offerings', offerings.filter((_, j) => j !== i))} className={`${sub} hover:text-rose-400 p-2`} aria-label="Remove offering"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={() => setList('offerings', [...offerings, { name: '', description: '', price: '', duration: '' }])} className="text-[12px] font-semibold text-teal-500" data-testid="add-offering-btn"><Plus size={12} className="inline -mt-0.5" /> Add offer</button>
      </div>);
    if (id === 'audience') return shell(id, Users, 'indigo',
      <AudienceRead customers={customers} isDarkMode={isDarkMode} />,
      <div>
        <Field ctx={ctx} section="customers" k="target_audience" label="Ideal customer" long />
        {lbl('Pain points')}<ChipsInput values={customers.pain_points || []} onChange={v => setIn('customers', 'pain_points', v)} placeholder="Add a pain point + Enter" accent="indigo" testId="chips-pain-points" />
        {lbl('Objections')}<ChipsInput values={customers.objections || []} onChange={v => setIn('customers', 'objections', v)} placeholder="Add an objection + Enter" accent="indigo" testId="chips-objections" />
      </div>);
    if (id === 'faqs') return shell(id, MessageCircleQuestion, 'sky',
      <FaqsRead faqs={faqs} isDarkMode={isDarkMode} />,
      <div className="space-y-4">
        {faqs.map((f, i) => (
          <div key={i} className={`grid gap-2 pb-4 border-b ${border}`}>
            <div className="flex gap-2"><input value={f.q} onChange={e => setList('faqs', faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} className={inputCls} placeholder="Question" />
              <button onClick={() => setList('faqs', faqs.filter((_, j) => j !== i))} className={`${sub} hover:text-rose-400 p-2`} aria-label="Remove FAQ"><Trash2 size={13} /></button></div>
            <textarea rows={2} value={f.a} onChange={e => setList('faqs', faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} className={inputCls} placeholder="Answer" />
          </div>
        ))}
        <button onClick={() => setList('faqs', [...faqs, { q: '', a: '', source: 'manual' }])} className="text-[12px] font-semibold text-teal-500" data-testid="add-faq-btn"><Plus size={12} className="inline -mt-0.5" /> Add FAQ</button>
      </div>);
    if (id === 'proof') return shell(id, Shield, 'emerald',
      <ProofRead proof={proof} gbp={gbp} isDarkMode={isDarkMode} />,
      <div>{[['guarantees', 'Guarantees'], ['certifications', 'Certifications'], ['awards', 'Awards'], ['review_themes', 'What customers praise']].map(([k, l]) => (
        <div key={k}>{lbl(l)}<ChipsInput values={proof[k] || []} onChange={v => setIn('proof', k, v)} placeholder={`Add ${l.toLowerCase()} + Enter`} accent="teal" testId={`chips-proof-${k}`} /></div>
      ))}</div>);
    if (id === 'voice') return shell(id, Mic2, 'cyan',
      <VoiceRead tone={tone} isDarkMode={isDarkMode} />,
      <div className="space-y-4">
        {TONE_AXES.map(([k, l, lo, hi]) => (
          <div key={k}><div className={`flex justify-between text-[11px] ${sub}`}><span>{l} · {lo}</span><span>{hi} · {tone[k] ?? 5}/10</span></div>
            <input type="range" min="0" max="10" value={tone[k] ?? 5} onChange={e => setIn('voice_tone', k, Number(e.target.value))} className="w-full accent-cyan-500" data-testid={`tone-slider-${k}`} /></div>
        ))}
        {lbl('Personality')}<ChipsInput values={tone.personality_adjectives || []} onChange={v => setIn('voice_tone', 'personality_adjectives', v)} placeholder="e.g. friendly + Enter" accent="cyan" testId="chips-personality" />
      </div>);
    if (id === 'guardrails') return shell(id, Ban, 'rose',
      <GuardrailsRead doNotSay={brand.do_not_say || []} competitors={brand.competitors || []} isDarkMode={isDarkMode} />,
      <div>
        {lbl('Never say')}<ChipsInput values={brand.do_not_say || []} onChange={v => set('do_not_say', v)} placeholder="e.g. 'guaranteed results' + Enter" accent="rose" testId="chips-do-not-say" />
        {lbl('Competitors')}<ChipsInput values={brand.competitors || []} onChange={v => set('competitors', v)} placeholder="Add a competitor + Enter" accent="fuchsia" testId="chips-competitors" />
      </div>);
    return shell(id, Palette, 'fuchsia', <LookRead colors={brand.colors} logo={brand.logo_url} isDarkMode={isDarkMode} />, null);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="brand-profile-section">

      <button onClick={onClose} className={`mb-5 inline-flex items-center gap-1.5 text-[13px] font-medium ${sub} hover:text-teal-500 transition-colors`} data-testid="brand-profile-back-btn">
        <ArrowLeft size={14} />All brand profiles
      </button>

      <ProfileHero brand={brand} name={info.name} isDarkMode={isDarkMode}
        scoreRing={<ScoreRing score={info.score} size={52} isDarkMode={isDarkMode} loading={running} />}
        actions={<>
          {projectMode && <button type="button" className="pm-button" disabled={dirty || saving || running} onClick={() => onSendToWizard?.(info.wizard_prompt || '')} data-testid="brand-profile-create-project">Create project</button>}
          {info.origin === 'onboarding' && (
            <button onClick={onOpenOnboardingData} className={`px-4 py-2.5 rounded-full border ${border} ${sub} text-xs font-semibold hover:text-teal-400 transition-colors`} data-testid="open-onboarding-data-btn">
              <FileText size={12} className="inline mr-1.5 -mt-0.5" />Onboarding data
            </button>
          )}
          {dirty && (
            <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold disabled:opacity-40 transition-colors" data-testid="brand-profile-save-btn">
              {saving ? <Loader2 size={12} className="inline mr-1.5 animate-spin" /> : <Save size={12} className="inline mr-1.5 -mt-0.5" />}Save changes
            </button>
          )}
          <div className="relative">
            <button onClick={() => setMenuOpen(v => !v)} className={`p-2.5 rounded-full border ${border} ${sub} hover:text-teal-400 transition-colors`} data-testid="brand-profile-menu-btn">
              <MoreVertical size={14} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
                <div className={`absolute right-0 top-12 z-30 rounded-2xl border p-1.5 min-w-[210px] shadow-2xl ${isDarkMode ? 'bg-[#0b1220]/95 backdrop-blur-xl border-white/10' : 'bg-white border-gray-200'}`}>
                  <button onClick={() => { setMenuOpen(false); setAnalyzeOpen(true); }} className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-semibold hover:bg-sky-400/10 transition-colors ${isDarkMode ? 'text-sky-300' : 'text-sky-600'}`} data-testid="open-analyze-btn">
                    <Globe size={12} className="inline mr-2 -mt-0.5" />{sources.identity ? 'Re-analyze website' : 'Analyze website'}
                  </button>
                  <button onClick={() => { setMenuOpen(false); setConfirmDelete(true); }} className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-semibold text-rose-400 hover:bg-rose-400/10 transition-colors" data-testid="delete-brand-profile-btn">
                    <Trash2 size={12} className="inline mr-2 -mt-0.5" />Delete brand profile
                  </button>
                </div>
              </>
            )}
          </div>
        </>} />

      {job && <AnalysisProgress job={job} url={url} isDarkMode={isDarkMode} onRetry={() => setJob(null)} />}

      <AnimatePresence>
        {analyzeOpen && !job && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className={`${card} rounded-2xl mb-8 overflow-hidden`} data-testid="analyze-panel">
            <div className="p-6 flex gap-3 flex-wrap items-center">
              <Globe size={16} className="text-teal-500" />
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://yourbusiness.com" className={`${inputCls} flex-1 min-w-[220px]`} data-testid="analyze-url-input" />
              <button onClick={startAnalysis} disabled={!url.trim() || (price && !price.sufficient)} className="px-6 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold disabled:opacity-40 transition-colors" data-testid="run-analysis-btn">Analyze website</button>
              <button onClick={() => setAnalyzeOpen(false)} className={`p-2 ${sub} hover:text-rose-400`} aria-label="Close" data-testid="analyze-close-btn"><X size={14} /></button>
              <p className={`basis-full text-[12px] ${sub}`} data-testid="analyze-price-note">
                {price && !price.unlimited && !price.sufficient ? 'Insufficient workspace balance — add funds to run an analysis. ' : ''}Fields you edited manually are never overwritten.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!(running && (info.score || 0) < 10) && (
        <div className="flex gap-10">
          <ProfileNav ids={visible} labels={labels} filled={filled} isDarkMode={isDarkMode} />
          <div className="flex-1 min-w-0 space-y-6">
            {missingTray.length > 0 && (
              <div className={`flex flex-wrap items-center gap-2 rounded-2xl border border-dashed px-5 py-4 ${border}`} data-testid="bp-missing-tray">
                <span className={`text-[12.5px] mr-1 ${sub}`}>Missing info:</span>
                {missingTray.map(id => (
                  <button key={id} type="button" onClick={() => toggleEdit(id)} className={`text-[12px] px-3 py-1 rounded-full border ${border} ${sub} hover:text-teal-500 hover:border-teal-400/50 transition-colors`} data-testid={`bp-missing-${id}`}>
                    <Plus size={11} className="inline -mt-0.5 mr-1" />{labels[id]}
                  </button>
                ))}
              </div>
            )}
            {visible.map(id => <React.Fragment key={id}>{renderSection(id)}</React.Fragment>)}
        {!projectMode && <WizardPromptCard api={api} subAccountId={subAccountId} profileId={profileId}
          initialPrompt={info.wizard_prompt || ''} isDarkMode={isDarkMode} onSendToWizard={onSendToWizard} />}
          </div>
        </div>
      )}

      {/* ── Confirm delete ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-delete-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className={`rounded-[24px] border p-6 max-w-md w-full ${isDarkMode ? 'bg-[#0b1220] border-white/10' : 'bg-white border-gray-200 shadow-2xl'}`}>
            <div className="w-11 h-11 rounded-2xl bg-rose-400/10 border border-rose-400/20 flex items-center justify-center mb-4">
              <Trash2 size={17} className="text-rose-400" />
            </div>
            <h3 className={`font-display text-lg font-semibold tracking-tight ${text}`}>Delete this brand profile?</h3>
            <p className={`text-xs mt-2 leading-relaxed ${sub}`}>
              This permanently clears everything the analysis built — identity, offerings, voice &amp; tone, FAQs and Google reviews.
              Onboarding form answers are kept. You can rebuild anytime by analyzing the website again.
            </p>
            <div className="flex gap-2 justify-end mt-6">
              <button onClick={() => setConfirmDelete(false)}
                className={`px-4 py-2.5 rounded-full border ${border} ${sub} text-xs font-semibold hover:text-teal-400 transition-colors`}
                data-testid="cancel-delete-btn">Cancel</button>
              <button onClick={deleteProfile} disabled={deleting}
                className="px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-red-500 text-white text-xs font-bold disabled:opacity-50 hover:-translate-y-0.5 transition-all"
                data-testid="confirm-delete-btn">
                {deleting ? <Loader2 size={12} className="inline mr-1.5 animate-spin" /> : null}Delete profile
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default BrandProfileSection;
